// Quartz Claims Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';

class ClaimsService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoint for quartz claims data
    this.claimsApiUrl = `${this.functionAppUrl}/api/quartz_claims`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get all quartz claims from database
   * Returns GeoJSON FeatureCollection for map rendering
   * @param {Object} options - Query options
   * @param {string} options.type - Type of claims: 'selkirk' (default) or 'nonselkirk'
   * @param {string} options.status - Filter by status (active, expired, etc.)
   * @param {string} options.owner - Filter by owner
   * @param {number} options.daysRemaining - Filter by days until expiry (Selkirk only)
   */
  async getAllClaims(options = {}) {
    try {
      const {
        type = 'selkirk',
        status = 'all',
        owner = 'all',
        daysRemaining = null
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.claimsApiUrl}`;
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add type parameter
      params.append('type', type);
      
      if (status && status !== 'all') {
        params.append('status', status);
      }
      if (owner && owner !== 'all') {
        params.append('owner', owner);
      }
      // Only add days_remaining for Selkirk claims
      if (daysRemaining !== null && type === 'selkirk') {
        params.append('days_remaining', daysRemaining);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('📡 Fetching claims from:', url, `(type: ${type})`);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error response:', response.status, errorText);
        throw new Error(`Failed to fetch claims data: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Claims data received:', result.data?.features?.length || 0, `${type} claims`);
      
      // Return GeoJSON FeatureCollection
      return result.data || { type: 'FeatureCollection', features: [] };
    } catch (error) {
      console.error('❌ Error fetching claims data:', error);
      throw error;
    }
  }

  /**
   * Get claims statistics and metrics
   * @param {string} type - Type of claims: 'selkirk' or 'nonselkirk'
   */
  async getClaimsStats(type = 'selkirk') {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await fetch(`${this.claimsApiUrl}?type=${type}&stats=true`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch claims stats: ${response.statusText}`);
      }

      const result = await response.json();
      return result.stats || {};
    } catch (error) {
      console.error('❌ Error fetching claims stats:', error);
      throw error;
    }
  }

  /**
   * Get unique owners for filtering
   * @param {string} type - Type of claims: 'selkirk' or 'nonselkirk'
   */
  async getOwners(type = 'selkirk') {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await fetch(`${this.claimsApiUrl}?type=${type}&owners=true`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch owners: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('❌ Error fetching owners:', error);
      throw error;
    }
  }

  /**
   * Create or update a claim
   * @param {Object} claimData - Claim data to save
   */
  async saveClaim(claimData) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const response = await fetch(this.claimsApiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(claimData)
      });

      if (!response.ok) {
        throw new Error(`Failed to save claim: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('❌ Error saving claim:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const claimsService = new ClaimsService();
export default claimsService;

